import uuid from 'react-uuid';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { getAnalytics, logEvent } from 'firebase/analytics';

import { getGwUrl, isProd } from '@config/getEnviroment';
import {
  getAppVersion,
  setGToken,
  getToken as getLocalToken,
  setGTokenId,
  getGTokenId,
  getGToken,
} from '@utils/localStorage';
import { getAppKey, getBrowserKey, getDeviceKey, getOsKey } from '@utils/device';
import {
  FAVORITE_ONBOARD_KEY,
  PROMOTION_ONBOARD_KEY,
  PROMOTION_POST_KEY,
  PROMOTIONS_ONBOARD_KEY,
  REGISTER_ONBOARD_KEY,
} from '@utils/onboarding';
import { getSessionKey } from '@utils/session';

const PLATFORM: string = 'monavis';
const COUNTRY: string = 'FR';
const LANGUAGE: string = 'fr';
const TIMEZONE: string = 'Europe/Paris';
const MINIMAL_CHAR_LENGTH: number = 10;
const MINIMAL_AGE: number = 18;

const NOTIFICATIONS_DELAY = 500;
const NOTIFICATIONS_DURATION = 10000 + NOTIFICATIONS_DELAY;

const getPlatform = (): string => PLATFORM;
const getCountry = (): string => COUNTRY;
const getLanguage = (): string => LANGUAGE;
const getMinimalCharLength = (): number => MINIMAL_CHAR_LENGTH;

const getBaseUrl = (): string => {
  return `${window.location.protocol}//${window.location.hostname}`;
};

const encryptDate = (date: string | null) => {
  if (!date) {
    return null;
  }

  if (date.length === 10) {
    const yearPart_1 = date.substr(0, 2);
    const yearPart_2 = date.substr(5, 2);
    const label = '**';
    const label_1 = '/';
    return label + label_1 + yearPart_2 + label_1 + yearPart_1 + label;
  }
  return date;
};

const getFirebaseConfig = (isProdArg: boolean): object => {
  if (!isProdArg) {
    return {
      apiKey: 'AIzaSyDpRQG8gXDC9QZFCcS3pHMpitE5RfcHDOo',
      authDomain: 'app-shopadvizor.firebaseapp.com',
      databaseURL: 'https://app-shopadvizor.firebaseio.com',
      projectId: 'app-shopadvizor',
      storageBucket: 'app-shopadvizor.appspot.com',
      messagingSenderId: '45903640042',
      appId: '1:45903640042:web:382e33456172f1220bee93',
      measurementId: 'G-N29K6RCNGB',
    };
  }
  return {
    apiKey: 'AIzaSyDepgs-sAsXiICFvLhG1V2bgzVehWU7OCs',
    authDomain: 'app-monavislerendgratuit.firebaseapp.com',
    databaseURL: 'https://app-monavislerendgratuit.firebaseio.com',
    projectId: 'app-monavislerendgratuit',
    storageBucket: 'app-monavislerendgratuit.appspot.com',
    messagingSenderId: '367177020782',
    appId: '1:367177020782:web:10bdd2ffe0a07192d53d7f',
    measurementId: 'G-S75C2E220M',
  };
};

function getVapidKey() {
  return 'BFkIoB2qkgQrFO3IgsPjVF6FMaAD9ekSbGGtzaS80rzTZQZbSwvi9-M1lcwHAQAFTlJ_eqE4UbaB4tEBPaQVmiw';
}

const gApp = initializeApp(getFirebaseConfig(isProd()));

const messaging = getMessaging(gApp);
const analytics = getAnalytics(gApp);

const customEvent = (event: string, params?: any) => {
  if (analytics) {
    const parsedParams = {
      browser: getBrowserKey(),
      os: getOsKey(),
      device: getDeviceKey(),
      ...params,
    };
    logEvent(analytics, event, parsedParams);
  }
};

const parseOnboardingKeyToFirebase = (key?: string) => {
  switch (key) {
    case PROMOTIONS_ONBOARD_KEY:
      return 'promotion_list';
    case PROMOTION_ONBOARD_KEY:
      return 'promotion_detail';
    case PROMOTION_POST_KEY:
      return 'first_order';
    case REGISTER_ONBOARD_KEY:
      return 'register';
    case FAVORITE_ONBOARD_KEY:
      return 'favorites';
    default:
      return key || '';
  }
};

const sendToken = async (token: string) => {
  if (token !== getGToken()) {
    const storageItem = localStorage.getItem('persist:root');
    const bearer = getLocalToken();
    if (storageItem && bearer) {
      const parsed = JSON.parse(storageItem);
      if (parsed?.auth) {
        const parsedAuth = JSON.parse(parsed.auth);
        if (parsedAuth?.userId) {
          const url = getGwUrl();
          const operationId = uuid();
          const device = getDeviceKey();
          const appVersion = getAppVersion();
          const appKey = getAppKey();
          const parsedVersion = appVersion ? appVersion.replaceAll('"', '') : appVersion;
          const headers = {
            'Content-Type': 'application/json',
            'X-Device': device,
            'X-App': appKey,
            'X-Version': parsedVersion,
            'X-Correlation-ID': operationId,
            'X-Session-ID': getSessionKey(),
            Authorization: `Bearer ${bearer}`,
          };

          const graphqlQuery = {
            operationName: 'CreateOrUpdateNotificationUserHasToken',
            query: `mutation CreateOrUpdateNotificationUserHasToken($input: CreateOrUpdateNotificationUserHasTokenInput!) {
              createOrUpdateNotificationUserHasToken(input: $input) {
                id
                token
              }
            }`,
            variables: {
              input: {
                id: getGTokenId() ?? uuid(),
                token,
                userId: parsedAuth.userId,
              },
            },
          };
          const options: any = {
            method: 'POST',
            headers,
            body: JSON.stringify(graphqlQuery),
          };

          if (url) {
            const response = await fetch(url, options);

            return response.json();
          }

          return false;
        }
      }
    }
  }

  return false;
};

async function requestPermission() {
  const permission = await Notification.requestPermission();

  if (permission === 'granted') {
    const token = await getToken(messaging, {
      vapidKey: getVapidKey(),
    });

    setGToken(token);
    sendToken(token)
      .then((res) => {
        if (res?.data?.createOrUpdateNotificationUserHasToken?.id) {
          setGTokenId(res?.data?.createOrUpdateNotificationUserHasToken?.id);
        }
        return true;
      })
      .catch(() => {});
  } else if (permission === 'denied') {
    // notifications are blocked
  }
}

export {
  TIMEZONE,
  NOTIFICATIONS_DELAY,
  NOTIFICATIONS_DURATION,
  getMinimalCharLength,
  getPlatform,
  getCountry,
  getLanguage,
  getBaseUrl,
  MINIMAL_AGE,
  getFirebaseConfig,
  encryptDate,
  MINIMAL_CHAR_LENGTH,
  gApp,
  messaging,
  customEvent,
  parseOnboardingKeyToFirebase,
  requestPermission,
};
